import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { auth } from '../api/auth'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { PinForm } from '../components/PinForm'

/**
 * `/set-pin?token=...` — pública, montada dentro de `AppShell` (footer +
 * topbar globales, ver CLAUDE.md). El token viaja en el link que manda el
 * mail de alta/reset; no requiere sesión.
 *
 * Flujo: PIN de 6 dígitos + confirmación → `POST /auth/pin/setup`. El link
 * equivale a la cuenta precreada por el club (mg, 2026-10-07): el backend
 * deja la sesión abierta con esa cuenta, sin alta ni elección de club. El
 * link se abre en el celu (el Quest no lee QR y tipear un link largo ahí no
 * es viable), así que al terminar la página dice qué poner en el visor:
 * código de club + el PIN. "Jugar en este dispositivo" lleva a `/` (puente
 * al juego) para quien sí lo abrió en el visor o la compu. Si el token vino
 * vencido/inválido (400/410), en vez del form se muestra un CTA para pedir un
 * mail nuevo (`POST /auth/pin/reset-request`, siempre 200 — no filtra si el
 * email existe).
 */
export function SetPin() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [tokenInvalid, setTokenInvalid] = useState(!token)
  const [done, setDone] = useState<{ loggedIn: boolean; clubName?: string; clubCode?: string } | null>(
    null,
  )

  const [resetEmail, setResetEmail] = useState('')
  const [resetBusy, setResetBusy] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  async function submitPin(pin: string) {
    const res = await auth.pinSetup(token, pin)
    if (res.logged_in) await refresh()
    setDone({ loggedIn: !!res.logged_in, clubName: res.club_name, clubCode: res.club_code })
  }

  function onPinError(err: unknown): boolean {
    if (err instanceof ApiError && (err.status === 400 || err.status === 410)) {
      setTokenInvalid(true)
      return false
    }
    return true
  }

  async function onRequestReset(e: FormEvent) {
    e.preventDefault()
    if (!resetEmail.includes('@') || resetBusy) return
    setResetBusy(true)
    try {
      await auth.pinResetRequest(resetEmail.trim().toLowerCase())
      setResetSent(true)
    } catch {
      // El backend siempre devuelve 200 acá — si esto falla es de red.
      setResetSent(true)
    } finally {
      setResetBusy(false)
    }
  }

  if (done) {
    return (
      <Shell>
        <h1 className="m-0">PIN listo</h1>
        <p className="mt-sm text-sm text-text-muted">
          Para entrar desde el visor o la tablet{done.clubName ? ` de ${done.clubName}` : ''}:
        </p>
        <ol className="mt-md flex list-none flex-col gap-sm p-0">
          <li className="card flex items-center justify-between gap-md">
            <span className="text-sm">1. Código de club</span>
            {done.clubCode ? (
              <code className="font-mono text-2xl tracking-[0.2em]">{done.clubCode}</code>
            ) : (
              <span className="text-sm text-text-muted">pedíselo al staff</span>
            )}
          </li>
          <li className="card flex items-center justify-between gap-md">
            <span className="text-sm">2. Tu PIN</span>
            <span className="font-mono text-2xl tracking-[0.3em]">••••••</span>
          </li>
        </ol>
        <p className="mt-sm text-xs text-text-muted">
          El código se pide una sola vez por visor; después alcanza con el PIN.
        </p>
        {done.loggedIn && (
          <button
            type="button"
            className="btn mt-md w-full"
            onClick={() => navigate('/', { replace: true })}
          >
            Jugar en este dispositivo
          </button>
        )}
      </Shell>
    )
  }

  if (tokenInvalid) {
    return (
      <Shell>
        <h1 className="m-0">Link vencido</h1>
        <p className="mt-sm text-sm text-text-muted">
          Este link para configurar tu PIN ya no es válido. Pedí uno nuevo con
          tu email.
        </p>

        {resetSent ? (
          <div className="banner-ok mt-md">Te mandamos un mail nuevo.</div>
        ) : (
          <form onSubmit={onRequestReset} className="mt-md flex flex-col gap-sm">
            <input
              type="email"
              value={resetEmail}
              onChange={(ev) => setResetEmail(ev.target.value)}
              placeholder="email@dominio.com"
              autoComplete="email"
              className="input"
              disabled={resetBusy}
            />
            <button
              type="submit"
              disabled={!resetEmail.includes('@') || resetBusy}
              className="btn-primary"
            >
              {resetBusy ? '…' : 'Mandar link nuevo'}
            </button>
          </form>
        )}

        <p className="mt-md text-sm">
          <Link to="/login">Ir al login →</Link>
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1 className="m-0">Configurá tu PIN</h1>
      <p className="mt-sm text-sm text-text-muted">
        Usás este PIN de 6 dígitos para entrar desde el casco, sin escribir
        tu contraseña.
      </p>

      <PinForm submitLabel="Guardar PIN y entrar" onSubmitPin={submitPin} onError={onPinError} />
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-lg py-2xl">
      <div className="panel max-w-md p-xl">{children}</div>
    </div>
  )
}
